import React, { useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Link from '@mui/material/Link';
import MuiCard from '@mui/material/Card';
import { styled } from '@mui/material/styles';
import InputAdornment from '@mui/material/InputAdornment';
import { gray } from 'src/templates/shared-theme/themePrimitives';
import { sendPrompt, sendProof, userBoundedLogin, userMe, setSessionToken } from 'src/api/api';
import { AppContext } from 'src/AppContext';

const Card = styled(MuiCard)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignSelf: 'center',
  width: '100%',
  padding: theme.spacing(4),
  gap: theme.spacing(2),
  margin: 'auto',
  [theme.breakpoints.up('sm')]: {
    maxWidth: '450px',
  },
  boxShadow:
    'hsla(220, 30%, 5%, 0.05) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.05) 0px 15px 35px -5px',
  ...theme.applyStyles('dark', {
    boxShadow:
      'hsla(220, 30%, 5%, 0.5) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.08) 0px 15px 35px -5px',
  }),
}));

const SignInContainer = styled(Stack)(({ theme }) => ({
  height: 'calc((1 - var(--template-frame-height, 0)) * 100dvh)',
  minHeight: '100%',
  padding: theme.spacing(2),
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(4),
  },
  '&::before': {
    content: '""',
    display: 'block',
    position: 'absolute',
    zIndex: -1,
    inset: 0,
    backgroundImage:
      'radial-gradient(ellipse at 50% 50%, hsl(210, 100%, 97%), hsl(0, 0%, 100%))',
    backgroundRepeat: 'no-repeat',
    ...theme.applyStyles('dark', {
      backgroundImage:
        'radial-gradient(at 50% 50%, hsla(210, 100%, 16%, 0.5), hsl(220, 30%, 5%))',
    }),
  },
}));

export default function SignIn({ signUp }) {
  const { setUser } = useContext(AppContext);

  const navigate = useNavigate();
  const location = useLocation();
  const [signinError, setSigninError] = React.useState(false);
  const [signinErrorMessage, setSigninErrorMessage] = React.useState('');
  const [countdown, setCountdown] = React.useState(0);
  const [promptID, setPromptID] = React.useState("");

  const handleSendOTP = async () => {
    resetError();
    if (!validateInputs() || countdown > 0) {
      return;
    }

    const phone = document.getElementById('phone');
    const phoneNumber = phone.value;

    const resp = await sendPrompt(phoneNumber);
    if (resp.error) {
      // handle error here.
      setSigninError(true);
      setSigninErrorMessage(resp.message);
      return;
    }

    setPromptID(resp.promptID);

    setCountdown(30);
    // Start countdown
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000); // Update every second
  };

  const handleSignin = async () => {
    resetError();
    if (!validateInputs()) {
      return;
    }
    if (promptID == "") {
      setSigninError(true);
      setSigninErrorMessage('Please click "send" to request OTP first');
      return;
    }

    const otpInput = document.getElementById('otp');
    const otp = otpInput.value;
    if (otp.length != 6) {
      setSigninError(true);
      setSigninErrorMessage('Invalid OTP');
      return;
    }

    const proofResp = await sendProof(promptID, otp);
    if (proofResp.error) {
      // handle error here.
      setSigninError(true);
      setSigninErrorMessage(proofResp.message);
      return;
    }

    if (proofResp.isNewPhone == true) {
      navigate("/createUser?promptID=" + promptID);
      return;
    }

    // handle login.
    const loginResp = await userBoundedLogin(promptID);
    if (loginResp.error) {
      // handle error here.
      setSigninError(true);
      setSigninErrorMessage(loginResp.message);
      return;
    }

    // save session
    setSessionToken(loginResp.sessionToken);
    // fetch user
    const user = await userMe();
    if (user.error) {
      setSigninError(true);
      setSigninErrorMessage(user.message);
      return;
    }
    // add user to context
    setUser(user);

    // just to home is sign up.
    if (signUp) {
      navigate("/");
      return;
    }
    
    // redirect to the last visited page or a default route for sign in.
    const redirectTo = location.state?.from?.pathname || '/';
    const search = location.state?.from?.search || '';
    navigate(redirectTo + search, { replace: true });
  };

  const resetError = () => {
    setSigninError(false);
    setSigninErrorMessage('');
  }

  const validateInputs = () => {
    const phone = document.getElementById('phone');

    let isValid = true;

    if (!phone.value || !/^[89]\d{7}$/.test(phone.value)) {
      setSigninError(true);
      setSigninErrorMessage('Please enter a valid phone number.');
      isValid = false;
    } else {
      resetError();
    }

    return isValid;
  }

  return (
    <SignInContainer direction="column" justifyContent="space-between">
      <Card variant="outlined">
        <Typography
          component="h1"
          variant="h4"
          sx={{ width: '100%', fontSize: 'clamp(2rem, 10vw, 2.15rem)' }}
        >
          { signUp ? "Sign up" : "Sign in" }
        </Typography>
        <Box
          noValidate
          sx={{
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            gap: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <TextField
              id="phone"
              type="number"
              name="phone"
              placeholder="Phone Number"
              autoComplete="phone"
              autoFocus
              required
              fullWidth
              variant="outlined"
              color={signinError ? 'error' : 'primary'}
              onChange={resetError}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Typography>+65</Typography>
                  </InputAdornment>
                ),
              }}
            />
            <Button
              id="optButton"
              variant="contained"
              color={countdown > 0 ? "inherit" : "secondary"}
              onClick={handleSendOTP}
              disabled={countdown > 0}
              sx={{
                marginLeft: 1,
                backgroundColor: countdown > 0 ? 'white' : 'secondary.main',
                color: countdown > 0 ? 'secondary.main' : 'white',
                border: countdown > 0 ? '1px solid' : 'none',
                borderColor: countdown > 0 ? 'secondary.main' : 'none',
                '&.Mui-disabled': {
                  backgroundColor: 'white',
                  color: 'secondary.main',
                },
              }}
            >
              {countdown > 0 ? `${countdown}s` : "Send"}
            </Button>
          </Box>
          <TextField
            error={signinError}
            helperText={signinErrorMessage}
            name="otp"
            placeholder="One Time Password"
            type="number"
            id="otp"
            required
            fullWidth
            variant="outlined"
            color='primary'
            onChange={resetError}
          />
          <Button
            fullWidth
            variant="contained"
            color="secondary"
            onClick={handleSignin}
          >
            { signUp ? "Sign up" : "Sign in" }
          </Button>
          {
            !signUp && 
            <>
              <Divider></Divider>
              <Typography sx={{ textAlign: 'center' }}>
              Don&apos;t have an account?{' '}
              <Link
                href="/signup"
                variant="body2"
                sx={{ alignSelf: 'center' }}
              >
                Sign up
              </Link>
            </Typography>
            </>
          }
        </Box>
      </Card>
    </SignInContainer>
  )
}
